import React, { useState } from 'react'
import {
  Box,
  FormControl,
  IconButton,
  InputBase,
  type InputBaseProps,
  InputLabel,
  Typography,
  styled,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'

import { CheckIcon } from '~assets/icons'
import { FormHelperText } from '~components/common'
import { Icon } from '~components/internal'

interface TextInputProps extends InputBaseProps {
  placeholder?: string
  label?: string
  helperText?: string
  errorText?: string
  showCopyButton?: boolean
  id?: string
  disableHelperText?: boolean
  isOptional?: boolean
}

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  width: '100%',
  position: 'relative',
  margin: theme.spacing(0, 0),
}))

const CopyButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: '8px',
  top: '50%',
  transform: 'translateY(-50%)',
  padding: '4px',
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}))

const TextInputBase = styled(InputBase)(({ theme, error, readOnly }) => ({
  width: '100%',
  'label + &': {
    marginTop: theme.spacing(3),
  },
  '& .MuiInputBase-input': {
    borderRadius: 4,
    position: 'relative',
    border: 'none',
    backgroundColor: readOnly ? theme.palette.action.disabledInput : 'white',
    color: readOnly ? theme.palette.text.secondary : theme.palette.text.primary,
    fontSize: 16,
    padding: '8.5px 12px',
    fontFamily: theme.typography.body2.fontFamily,
    '&::placeholder': {
      color: theme.palette.text.secondary,
      opacity: 1,
    },
    boxShadow: readOnly
      ? `inset 0 0 0 1px ${theme.palette.text.disabled}`
      : error
        ? `inset 0 0 0 2px ${theme.palette.error.main}`
        : `inset 0 0 0 1px ${theme.palette.input.default}`,
    transition: theme.transitions.create(['box-shadow']),
    '&:invalid': {
      boxShadow: error
        ? `inset 0 0 0 2px ${theme.palette.error.main}`
        : `inset 0 0 0 1px ${theme.palette.input.default}`,
    },
    '&:hover': !readOnly && {
      boxShadow: `inset 0 0 0 2px ${theme.palette.input.hover}`,
    },

    '&:focus-visible': !readOnly && {
      boxShadow: `inset 0 0 0 2px ${theme.palette.input.hover}, 0 0 0 2px ${theme.palette.input.focus}`,
      outline: 'none',
    },
  },
}))

export const InputLabelStyled = styled(InputLabel)(({ theme }) => ({
  fontSize: 16,
  letterSpacing: theme.typography.body2.letterSpacing,
}))

const TextInput: React.FC<TextInputProps> = ({
  label,
  placeholder = '',
  type,
  helperText,
  errorText = 'Input Required',
  showCopyButton = false,
  id,
  disableHelperText = false,
  isOptional = false,
  ...props
}) => {
  const [copied, setCopied] = useState<boolean>(false)
  const wheelHandler =
    type === 'number'
      ? { onWheel: (e: React.WheelEvent): void => (e.target as HTMLElement).blur() }
      : {}
  const theme = useTheme()

  const handleCopy = async (): Promise<void> => {
    if (props.value) {
      try {
        await navigator.clipboard.writeText(props.value.toString())
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      } catch (err) {
        console.error('Failed to copy text:', err)
      }
    }
  }

  return (
    <StyledFormControl variant='standard'>
      {label && (
        <InputLabelStyled
          htmlFor={id || 'text-input'}
          shrink
          sx={{
            fontSize: 20,
            color: props.error ? theme.palette.error.dark : theme.palette.text.secondary,
            '&.Mui-focused': {
              color: props.readOnly ? theme.palette.text.secondary : theme.palette.text.primary,
            },
          }}
        >
          {label}
        </InputLabelStyled>
      )}
      <TextInputBase
        id={id || 'text-input'}
        placeholder={placeholder}
        type={type}
        inputProps={{ min: 0 }}
        tabIndex={props.readOnly ? -1 : props.tabIndex}
        autoFocus={props.autoFocus}
        aria-disabled={props.readOnly}
        {...wheelHandler}
        {...props}
      />
      {isOptional && (
        <Typography
          variant='body2'
          sx={{
            color: theme.palette.text.disabled,
            fontWeight: 'bold',
            position: 'absolute',
            right: 0,
            top: 0,
          }}
        >
          Optional
        </Typography>
      )}
      {props.readOnly && showCopyButton && (
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: label ? '46px' : '22px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <CopyButton onClick={handleCopy} size='small' aria-label='copy text'>
            {copied ? <CheckIcon width={16} /> : <Icon name='copy' fontSize='small' fill={0} />}
          </CopyButton>
        </Box>
      )}
      {/* Always render FormHelperText with consistent height to prevent layout shift */}
      {!disableHelperText && (
        <FormHelperText error={props.error || false} isVisible={props.error || !!helperText} icon>
          {props.error ? errorText : helperText || ' '}
        </FormHelperText>
      )}
    </StyledFormControl>
  )
}

export default TextInput
